import {
  isPrivateBucketFileDownloadUrl,
  isPresignedS3Url,
  stripAccessTokenFromPrivateFileUrl
} from "@/lib";
import type {
  PrivateFileRecord,
  PrivateFileTool
} from "@/helpers/private-file-url";
import { getPrivateFileDownloadUrl } from "@/helpers/private-file-url";

/**
 * URL stored on answers / marker-response / ideas for the backend.
 * Always the app download path (`/survey_files/.../download` or
 * `/idea_files/.../download`), never presigned S3 and never display tokens.
 */
export function getPrivateFileSubmissionUrl(
  fileUrl: string | undefined
): string | undefined {
  if (!fileUrl) return fileUrl;
  if (isPresignedS3Url(fileUrl)) return undefined;

  if (isPrivateBucketFileDownloadUrl(fileUrl)) {
    return stripAccessTokenFromPrivateFileUrl(fileUrl);
  }

  return fileUrl;
}

/** Resolve a safe submit URL from an API / upload record. */
export function getPrivateFileSubmissionUrlFromRecord(
  source: PrivateFileRecord,
  tool: PrivateFileTool = "survey"
): string | undefined {
  const downloadUrl = getPrivateFileDownloadUrl(source, tool);
  return getPrivateFileSubmissionUrl(downloadUrl);
}

/** Strip client-only fields before survey / API submit. */
export function sanitizeFileQuestionAnswerForSubmit(
  answer: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!answer) return answer;

  const rest = Object.fromEntries(
    Object.entries(answer).filter(
      ([key]) => key !== "presigned_file_url" && key !== "presignedFileUrl"
    )
  );

  const value = rest.value as string | undefined;

  return {
    ...rest,
    value: getPrivateFileSubmissionUrl(value)
  };
}

export function sanitizeSurveyAnswersForSubmit(
  answers: Record<string, unknown>,
  questions: Record<string, { type?: string }>
): Record<string, unknown> {
  return Object.entries(answers).reduce(
    (acc, [questionId, answer]) => {
      if (questions[questionId]?.type === "FileQuestion") {
        acc[questionId] = sanitizeFileQuestionAnswerForSubmit(
          answer as Record<string, unknown>
        );
      } else {
        acc[questionId] = answer;
      }
      return acc;
    },
    {} as Record<string, unknown>
  );
}
